<script setup lang="ts">
import {ExtendedProject, Role} from "@/types";
import BaseLayout from "@/layouts/BaseLayout.vue";
import ProjectLayout from "@/Pages/projects/layouts/ProjectLayout.vue";
import {route} from "ziggy-js";
import {Head, InertiaForm, useForm} from "@inertiajs/vue3";
import TextField from "@/components/forms/TextField.vue";
import ChangeVisibilityDialog from "@/Pages/projects/components/ChangeVisibilityDialog.vue";
import DeleteProjectDialog from "@/Pages/projects/components/DeleteProjectDialog.vue";

interface Props {
    project: ExtendedProject,
    role: Role,
}

const props = defineProps<Props>()
defineOptions({
    layout: BaseLayout
})

const generalForm = useForm({
    name: props.project.name,
    description: props.project.description,
})

const hasGroups = useForm({
    has_groups: props.project.has_groups,
})


const hostForm = useForm({
    host: props.project.host,
    port: props.project.port,
    default_login: props.project.default_login,
    upload_path: props.project.upload_path,
})

const submit = (form: InertiaForm<Partial<ExtendedProject>>) => {
    form.patch(route('projects.update', {project: props.project.slug}), {
        preserveScroll: true,
    })
}
</script>

<template>
    <Head title="Edit project" />
    <project-layout
        :role="props.role"
        :project="props.project"
    >
        <h1 class="font-weight-medium text-h5 mt-5">
            General
        </h1>
        <v-divider class="mt-3" />
        <v-card
            :flat="true"
            class="mb-5"
        >
            <v-card-text class="px-0">
                <v-form @submit.prevent="submit(generalForm)">
                    <v-row class="mt-3">
                        <v-col
                            cols="10"
                        >
                            <v-row :dense="true">
                                <v-col cols="12">
                                    <text-field
                                        v-model="generalForm.name"
                                        label="Name"
                                        :errors="generalForm.errors.name"
                                        :required="true"
                                    />
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                    <v-row class="mt-0">
                        <v-col
                            cols="10"
                        >
                            <v-row :dense="true">
                                <v-col cols="12">
                                    <text-field
                                        v-model="generalForm.description"
                                        label="Description"
                                        :errors="generalForm.errors.description"
                                    />
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col cols="10">
                            <submit-btn
                                :loading="generalForm.processing"
                                type="submit"
                            >
                                <span class="font-weight-bold">
                                    Save changes
                                </span>
                            </submit-btn>
                            <v-icon
                                v-if="generalForm.wasSuccessful"
                                color="success"
                                class="ml-2"
                            >
                                mdi-check
                            </v-icon>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </v-card>
        <h1 class="font-weight-medium text-h5 mt-5">
            SFTP
        </h1>
        <v-divider class="mt-3" />
        <p class="text-body-2 mt-5">
            The SFTP credentials are used to connect to the remote server and transfer files. The credentials are shared with all members of the project.
        </p>
        <v-card
            :flat="true"
            class="mb-5"
        >
            <v-card-text class="px-0">
                <v-form @submit.prevent="submit(hostForm)">
                    <v-row class="mt-3">
                        <v-col
                            cols="10"
                        >
                            <v-row :dense="true">
                                <v-col cols="9">
                                    <text-field
                                        v-model="hostForm.host"
                                        label="Host"
                                        :errors="hostForm.errors.host"
                                        :required="true"
                                    />
                                </v-col>
                                <v-col
                                    cols="3"
                                    align-self="center"
                                >
                                    <text-field
                                        v-model="hostForm.port"
                                        label="Port"
                                        type="number"
                                        :errors="hostForm.errors.port"
                                        :required="true"
                                    />
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                    <v-row class="mt-0">
                        <v-col
                            cols="10"
                        >
                            <v-row :dense="true">
                                <v-col cols="12">
                                    <text-field
                                        v-model="hostForm.upload_path"
                                        label="Remote path"
                                        :errors="hostForm.errors.upload_path"
                                    />
                                </v-col>
                                <v-col
                                    cols="12"
                                >
                                    <text-field
                                        v-model="hostForm.default_login"
                                        label="Username (default login)"
                                        :errors="hostForm.errors.default_login"
                                        :required="!props.project.has_groups"
                                    />
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col cols="10">
                            <submit-btn
                                :loading="hostForm.processing"
                                type="submit"
                            >
                                <span class="font-weight-bold">
                                    Update credentials
                                </span>
                            </submit-btn>
                            <v-icon
                                v-if="hostForm.wasSuccessful"
                                color="success"
                                class="ml-2"
                            >
                                mdi-check
                            </v-icon>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </v-card>
        <h1 class="font-weight-medium text-h5 mt-5">
            Features
        </h1>
        <v-list
            class="border pb-0 mt-3"
            rounded="lg"
        >
            <v-list-item
                lines="two"
            >
                <template #prepend>
                    <v-checkbox-btn
                        v-model="hasGroups.has_groups"
                        class="pb-12"
                        @change="submit(hasGroups)"
                    >
                        <template
                            #label
                        >
                            <v-progress-circular
                                v-if="hasGroups.processing"
                                :indeterminate="true"
                                size="x-small"
                                class="mx-1"
                            />
                            <v-icon
                                v-if="hasGroups.wasSuccessful"
                                color="success"
                                size="small"
                                class="mr-1"
                            >
                                mdi-check
                            </v-icon>
                        </template>
                    </v-checkbox-btn>
                </template>
                <v-list-item-title class="text-body-1 font-weight-bold">
                    Groups
                </v-list-item-title>
                <v-list-item-subtitle class="text-body-2 lh-1-5">
                    <p>
                        By activating groups, you allow multiple users per SSH login. This can be useful for projects with multiple users and SSH logins. <a
                            href="#"
                            class="text-blue"
                        >Learn more about groups.</a>
                    </p>
                </v-list-item-subtitle>
            </v-list-item>
        </v-list>
        <h1 class="font-weight-medium text-h5 mt-5">
            Danger Zone
        </h1>
        <v-list
            class="border border-red pb-0 mt-3 mb-10"
            rounded="lg"
        >
            <v-list-item
                lines="two"
                class="pb-5 border-b"
            >
                <v-list-item-title class="text-body-2 font-weight-bold">
                    Change project visibility
                </v-list-item-title>
                <v-list-item-subtitle class="text-body-2 lh-1-5">
                    <p>This project is currently {{ props.project.is_public ? 'public' : 'private' }}.</p>
                </v-list-item-subtitle>
                <template #append>
                    <change-visibility-dialog
                        :project="props.project"
                    />
                </template>
            </v-list-item>
            <v-list-item
                lines="two"
                class="pb-5"
            >
                <v-list-item-title class="text-body-2 font-weight-bold">
                    Delete this project
                </v-list-item-title>
                <v-list-item-subtitle class="text-body-2 lh-1-5">
                    <p>Once you delete a project, there is no going back. Please be certain.</p>
                </v-list-item-subtitle>
                <template #append>
                    <delete-project-dialog :project="props.project" />
                </template>
            </v-list-item>
        </v-list>
    </project-layout>
</template>

<style scoped>
.lh-1-5 {
 line-height: 1.5rem !important;
}

.border-red {
    border-color: rgba(244,67,54,0.3) !important;
}
</style>
